import { useState } from 'react';
import { AlertTriangle, GitMerge, Loader2 } from 'lucide-react';
import { diffText, withContext, type DiffLine } from '../rdf/diff';
import type { UnsupportedConstruct } from '../rdf/unsupported';

interface Props {
  original: string;
  updated: string;
  unsupported: UnsupportedConstruct[];
  saving: boolean;
  error: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

function prefix(line: DiffLine): string {
  if (line.kind === 'added') return '+';
  return line.kind === 'removed' ? '-' : ' ';
}

export function SaveDialog({ original, updated, unsupported, saving, error, onConfirm, onCancel }: Props) {
  const [showAll, setShowAll] = useState(false);
  const diff = diffText(original, updated);
  const lines = showAll ? diff.lines : withContext(diff);
  const unchanged = diff.added === 0 && diff.removed === 0;

  return (
    <div className="oe-modal-backdrop" role="dialog" aria-modal="true" aria-label="Review changes">
      <div className="oe-modal">
        <header className="oe-modal-header">
          <h2>Review changes</h2>
          <span className="oe-diff-stat">
            <span className="oe-added">+{diff.added}</span> <span className="oe-removed">−{diff.removed}</span>
          </span>
        </header>

        {unsupported.length > 0 && (
          <div className="oe-warning">
            <AlertTriangle size={16} />
            <div>
              <strong>The editor cannot represent everything in this file.</strong>
              <p>Saving drops the following, as the diff below shows:</p>
              <ul>
                {unsupported.map((construct) => (
                  <li key={construct.label}>
                    {construct.label} <span className="oe-muted">×{construct.count}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {unchanged
          ? <p className="oe-muted oe-diff-empty">The file is byte-identical to the one loaded. Nothing to commit.</p>
          : (
            <pre className="oe-diff">
              {lines.map((line, index) => (
                <div key={index} className={`oe-diff-line oe-${line.kind}`}>
                  <span className="oe-diff-gutter">{line.originalLine ?? ''}</span>
                  <span className="oe-diff-gutter">{line.updatedLine ?? ''}</span>
                  <span>{prefix(line)}{line.text}</span>
                </div>
              ))}
            </pre>
          )}

        {!unchanged && !showAll && diff.lines.length > lines.length && (
          <button className="oe-link-btn" onClick={() => setShowAll(true)}>
            Show all {diff.lines.length} lines
          </button>
        )}

        {error && <p className="oe-error">{error}</p>}

        <footer className="oe-modal-footer">
          <button className="oe-btn" onClick={onCancel} disabled={saving}>Cancel</button>
          <button className="oe-btn oe-btn-primary" onClick={onConfirm} disabled={saving || unchanged}>
            {saving ? <Loader2 size={14} className="oe-spin" /> : <GitMerge size={14} />}
            {saving ? 'Committing…' : 'Commit and open merge request'}
          </button>
        </footer>
      </div>
    </div>
  );
}
